import React, { useEffect, useState } from 'react';
import {
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import { IconButton } from '@mui/material';
import { Columns } from '@/types';
import {
  AllAssemblyTemplatesForFilterSideBarDocument,
  SortTypeEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TemplateTypeEnum,
  useUpdatePlannerColumnOptionsMutation,
  useUpdateTemplateMutation,
} from '@/__generated__/graphql';
import { GenericModal } from '@/components/shared/GenericModal';
import { dialogOverlay } from '../NewComponent/styles';
import { formatEnumForDisplay, LowerSnakeCase, mapDisplayTypeToColumnType } from '@/utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtomValue, useSetAtom } from 'jotai';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { useSelectedAssemblyTemplateId } from '../../hooks';
import { Delete as DeleteIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import * as analysisState from '@/components/Analysis/state';
import { prepareTemplateFields } from '@/hooks/usePrepareTemplateFields';

type TrackedOption = { original: string | null; current: string };

type Props = {
  open: boolean;
  initialColumns: Columns[];
  onClose: () => void;
  onCreate: (col: Columns) => void;
  initialColumn?: Columns | null;
  onUpdate?: (col: Columns) => void;
};

export const ColumnModal = ({
  open,
  onClose,
  onCreate,
  initialColumns,
  initialColumn,
  onUpdate,
}: Props) => {
  const [newColumnName, setNewColumnName] = useState('');
  const structureId = useGetStructureId();
  const [displayType, setDisplayType] = useState<TableCellStyleEnum>(TableCellStyleEnum.Text);
  const analysisAssemblyTemplate = useAtomValue(analysisState.assemblyTemplate);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [dropdownOptions, setDropdownOptions] = useState<TrackedOption[]>([
    { original: null, current: '' },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const setRefetchEquipmentTable = useSetAtom(analysisState.refetchEquipmentTable);
  const [addNewColumn] = useUpdateTemplateMutation({
    onCompleted: (response) => {
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while adding column', 'error');
        return;
      }
      showSnackBar('Columns added successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });
  const [updateColumnOptions] = useUpdatePlannerColumnOptionsMutation({
    onCompleted: (response) => {
      if (!response?.updatePlannerColumnOptions) {
        showSnackBar('An error occurred while updating column options', 'error');
        return;
      }
      showSnackBar('Column options updated successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });
  const handleAddDropdownOption = () =>
    setDropdownOptions((d) => [...d, { original: null, current: '' }]);
  const handleRemoveDropdownOption = (index: number) =>
    setDropdownOptions((d) => d.filter((_, i) => i !== index));
  const handleChangeDropdownOption = (index: number, value: string) =>
    setDropdownOptions((d) =>
      d.map((option, i) => (i === index ? { ...option, current: value } : option))
    );

  useEffect(() => {
    if (initialColumn) {
      setNewColumnName(initialColumn.columnName ?? initialColumn.columnName ?? '');
      setDisplayType(
        (initialColumn.displayConfig?.type as TableCellStyleEnum) ?? TableCellStyleEnum.Text
      );
      const opts: TrackedOption[] =
        initialColumn.options && initialColumn.options.length > 0
          ? initialColumn.options.map((value) => ({ original: value, current: value }))
          : [{ original: null, current: '' }];
      setDropdownOptions(opts);
      return;
    }
    if (open) {
      setNewColumnName('');
      setDisplayType(TableCellStyleEnum.Text);
      setDropdownOptions([{ original: null, current: '' }]);
    }
  }, [initialColumn, open]);

  const handleConfirm = async () => {
    if (!newColumnName) return;
    const trimmedColumnName = newColumnName.trim();
    const { cleanedFields, preventPlannerDuplicateField } = prepareTemplateFields(
      analysisAssemblyTemplate?.fields
    );
    if (
      preventPlannerDuplicateField(
        { ...initialColumn, columnName: trimmedColumnName } as Columns,
        initialColumns,
        true,
        initialColumn?.templateFieldKey
      )
    ) {
      showSnackBar('Column already Exists', 'error');
      return;
    }

    const key = LowerSnakeCase(trimmedColumnName);
    const mappedType = mapDisplayTypeToColumnType(displayType);

    const prevOptions = initialColumn?.options ?? [];
    const currentOptions =
      displayType === TableCellStyleEnum.Dropdown
        ? dropdownOptions.filter((opt) => opt.current).map((opt) => opt.current)
        : [];

    const optionsMap: Record<string, string | null> = {};

    // Handle renamed options
    for (const option of dropdownOptions) {
      if (option.original && option.current && option.original !== option.current) {
        optionsMap[option.original] = option.current;
      }
    }

    // Handle deleted options
    const currentOriginals = new Set(dropdownOptions.map((opt) => opt.original).filter(Boolean));
    for (const prevOption of prevOptions) {
      if (!currentOriginals.has(prevOption) && !(prevOption in optionsMap)) {
        optionsMap[prevOption] = null;
      }
    }

    const optionsMapPayload = Object.keys(optionsMap).length ? optionsMap : undefined;

    const columnPayload = {
      name: trimmedColumnName,
      key,
      type: mappedType,
      displayConfig: {
        id: (initialColumn?.displayConfig as any)?.id ?? generateMongoObjectId(),
        type: displayType,
        sortType:
          mappedType !== TemplateFieldTypeEnum.Numeric ? SortTypeEnum.String : SortTypeEnum.Number,
        isSortable: initialColumn?.isSortable ?? true,
      },
      options: displayType === TableCellStyleEnum.Dropdown ? currentOptions : undefined,
      optionsMap: optionsMapPayload,
      displayInQuery:
        displayType === TableCellStyleEnum.Dropdown ? currentOptions.length > 0 : false,
      enableInlineEdit: true,
      serverSide: false,
      isPlannerColumn: initialColumn?.isPlannerColumn ?? true,
    };

    // If creating new column
    if (!initialColumn) {
      if (
        preventPlannerDuplicateField(
          { ...columnPayload, columnName: newColumnName, isSortable: false, visibility: true },
          initialColumns,
          false
        )
      ) {
        showSnackBar('Column already Exists', 'error');
        return;
      }

      onCreate({
        ...columnPayload,
        columnName: trimmedColumnName,
        isSortable: false,
        visibility: true,
        templateFieldKey: key,
        isTemplateField: true,
      });

      try {
        setIsLoading(true);
        await addNewColumn({
          variables: {
            input: {
              type: TemplateTypeEnum.Assembly,
              structureId,
              templateId: selectedAssemblyTemplateId!,
              fields: [...cleanedFields, columnPayload],
              lastModifiedFor: 'Update Fields',
            },
          },
          refetchQueries: [AllAssemblyTemplatesForFilterSideBarDocument],
        }).then(() => {
          setRefetchEquipmentTable((s) => (s ?? 0) + 1);
          setNewColumnName('');
          setDisplayType(TableCellStyleEnum.Text);
          setDropdownOptions([{ original: null, current: '' }]);
          setIsLoading(false);
          onClose();
        });
      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
      return;
    } else {
      try {
        setIsLoading(true);
        await updateColumnOptions({
          variables: {
            input: {
              type: TemplateTypeEnum.Assembly,
              structureId,
              templateId: selectedAssemblyTemplateId!,
              fieldKey: key,
              field: columnPayload,
            },
          },
          refetchQueries: [AllAssemblyTemplatesForFilterSideBarDocument],
        }).then(() => {
          // cheap method to refetch equipment table
          setRefetchEquipmentTable((s) => (s ?? 0) + 1);
          setNewColumnName('');
          setDisplayType(TableCellStyleEnum.Text);
          setDropdownOptions([{ original: null, current: '' }]);
          setIsLoading(false);

          if (onUpdate) {
            onUpdate({
              ...columnPayload,
              columnName: newColumnName,
              isSortable: initialColumn?.isSortable ?? false,
              visibility: initialColumn?.visibility ?? true,
              templateFieldKey: initialColumn?.templateFieldKey,
              isTemplateField: true,
            });
          }

          onClose();
        });
      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
    }
  };

  return (
    <GenericModal
      open={open}
      handleClose={onClose}
      title={initialColumn ? 'Edit column' : 'New column'}
      description={
        'Use custom columns to capture additional information in your table. Choose a column name and select the type of values it will contain.'
      }
      titleSx={{ fontWeight: 600 }}
      backgroundPaperStyle={{
        minHeight: '30rem',
        display: 'flex',
        flexDirection: 'column',
      }}
      sx={{ ...dialogOverlay, height: 'fit-content' }}
    >
      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Stack spacing={2} sx={{ width: '50rem' }}>
          <TextField
            label="Column name"
            value={newColumnName}
            onChange={(e) => setNewColumnName(e.target.value)}
            fullWidth
          />
          {!initialColumn && (
            <Stack direction="row" spacing={2}>
              <Stack spacing={2} direction="row" sx={{ width: '60rem' }}>
                <FormControl fullWidth>
                  <InputLabel id="display-type-label">Display type</InputLabel>
                  <Select
                    labelId="display-type-label"
                    value={displayType}
                    label="Display type"
                    onChange={(e) => setDisplayType(e.target.value as TableCellStyleEnum)}
                  >
                    {[
                      TableCellStyleEnum.Date,
                      TableCellStyleEnum.Dropdown,
                      TableCellStyleEnum.Number,
                      TableCellStyleEnum.Text,
                    ].map((val) => (
                      <MenuItem key={val} value={val}>
                        {formatEnumForDisplay(val)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          )}

          {displayType === TableCellStyleEnum.Dropdown && (
            <Box sx={{ maxHeight: '28rem', overflowY: 'auto', pr: 1 }}>
              {dropdownOptions.map((opt, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TextField
                    placeholder={`Value ${String(idx + 1).padStart(2, '0')}`}
                    value={opt.current}
                    onChange={(e) => handleChangeDropdownOption(idx, e.target.value)}
                    fullWidth
                  />
                  <IconButton
                    onClick={() => handleRemoveDropdownOption(idx)}
                    aria-label="remove-option"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
              <Button onClick={handleAddDropdownOption} startIcon={<AddIcon />}>
                Add option
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 5 }}>
        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Confirm'}
        </Button>
      </DialogActions>
    </GenericModal>
  );
};
